/**
 * Blockly 工具箱定义
 * @module features/blockly/toolbox
 */

import { getResolvedUiLanguageFromStorage, translateUiText, type ResolvedUiLanguage } from '@/i18n';
import type { LanguageId } from '@/types';
import { MQTT_RUNTIME_PASSWORD_PLACEHOLDER } from '@/utils';

/** 工具箱分类ID */
export type CategoryId =
  | 'logic'
  | 'loops'
  | 'math'
  | 'text'
  | 'variables'
  | 'functions'
  | 'lists'
  | 'colour';

/** 分类信息 */
export interface CategoryInfo {
  id: CategoryId;
  name: string;
  colour: string;
}

/** 所有分类定义 */
export const CATEGORIES: CategoryInfo[] = [
  { id: 'logic', name: '逻辑', colour: '#5b80a5' },
  { id: 'loops', name: '循环', colour: '#5ba55b' },
  { id: 'math', name: '数学', colour: '#5b67a5' },
  { id: 'text', name: '文本', colour: '#5ba58c' },
  { id: 'variables', name: '变量', colour: '#a55b80' },
  { id: 'functions', name: '函数', colour: '#995ba5' },
  { id: 'lists', name: '列表', colour: '#745ba5' },
  { id: 'colour', name: '颜色', colour: '#a5745b' },
];

/** 工具箱版本号 */
export const TOOLBOX_VERSION = '1.0.0';

const TOOLBOX_LOCALIZABLE_ATTRIBUTE_TEXTS = [
  '标准',
  '逻辑',
  '循环',
  '数学',
  '文本',
  'JSON',
  '变量',
  '函数',
  '列表',
  '颜色',
  'IoT',
  'WiFi',
  'MQTT',
  '电脑',
  '3D',
  '无线外设',
  '有线外设',
  'UI',
  'yiOS',
  'C语言',
  '字符串',
  '标准IO',
  '舞台',
  '舞台设置',
  '控制',
  '运动',
  '外观',
  '声音',
  '事件',
  '侦测',
  '画笔',
  '音乐',
  '视频',
  '硬件',
  '请先在左侧外设中添加无线外设',
  '请先在左侧外设中添加有线外设',
] as const;

const replaceAllText = (input: string, search: string, replacement: string): string =>
  input.split(search).join(replacement);

const escapeXmlAttribute = (value: string): string => replaceAllText(
  replaceAllText(
    replaceAllText(
      replaceAllText(value, '&', '&amp;'),
      '"',
      '&quot;'
    ),
    '<',
    '&lt;'
  ),
  '>',
  '&gt;'
);

const localizeToolboxAttributeText = (text: string, uiLanguage: ResolvedUiLanguage): string =>
  escapeXmlAttribute(translateUiText(text, uiLanguage));

const localizeToolboxXmlAttributes = (
  xml: string,
  attributeName: 'name' | 'text',
  uiLanguage: ResolvedUiLanguage
): string => TOOLBOX_LOCALIZABLE_ATTRIBUTE_TEXTS.reduce(
  (currentXml, text) => replaceAllText(
    currentXml,
    `${attributeName}="${text}"`,
    `${attributeName}="${localizeToolboxAttributeText(text, uiLanguage)}"`
  ),
  xml
);

const localizeToolboxXml = (xml: string, uiLanguage: ResolvedUiLanguage): string => {
  const withLocalizedNames = localizeToolboxXmlAttributes(xml, 'name', uiLanguage);
  return localizeToolboxXmlAttributes(withLocalizedNames, 'text', uiLanguage);
};

const localizeCategoryName = (name: string, uiLanguage: ResolvedUiLanguage): string =>
  translateUiText(name, uiLanguage);

export function getWirelessVariableButtonCallbackKey(peripheralId: string): string {
  const normalized = peripheralId.replace(/[^a-zA-Z0-9]/g, '_');
  return `CREATE_WIRELESS_VAR_${normalized}`;
}

export function getWirelessPeripheralCategoryCallbackKey(peripheralId: string): string {
  const normalized = peripheralId.replace(/[^a-zA-Z0-9]/g, '_');
  return `WIRELESS_PERIPHERAL_CATEGORY_${normalized}`;
}

export function getYcUiCategoryCallbackKey(): string {
  return 'YC_UI_CATEGORY';
}

export function getYcUiVariableButtonCallbackKey(kind: string): string {
  const normalized = kind.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
  return `CREATE_YC_UI_VAR_${normalized}`;
}

/**
 * 生成工具箱XML
 */
export function getToolboxXml(
  language?: LanguageId,
  supportedLanguages?: LanguageId[] | null,
  selectedPeripheralIds: string[] = [],
  selectedControllerModel?: string | null,
  isLocalController = false,
  uiLanguage: ResolvedUiLanguage = getResolvedUiLanguageFromStorage()
): string {
  const shouldShowCLanguage = Boolean(supportedLanguages?.includes('c'));
  const shouldShowYiOsCategory = !isLocalController && /^YC-/i.test(selectedControllerModel || '');
  const selectedWirelessPeripheralIds = selectedPeripheralIds.filter((id) => id.startsWith('YM-'));
  const selectedWiredPeripheralIds = selectedPeripheralIds.filter((id) => id.startsWith('YN-'));

  const wirelessPeripheralGroups = selectedWirelessPeripheralIds
    .map((id) => `
      <category name="${id}" colour="#2f8fdb" custom="${getWirelessPeripheralCategoryCallbackKey(id)}"></category>`)
    .join('');

  const wiredPeripheralBlocks = selectedWiredPeripheralIds
    .map((id) => `
      <block type="wired_peripheral_action">
        <field name="PERIPHERAL">${id}</field>
      </block>`)
    .join('');
  const cStdLibCategory = shouldShowCLanguage && language === 'c' ? `
      <category name="数学" colour="#8c6b5c">
        <label text="math.h"></label>
        <block type="math_single">
          <value name="NUM">
            <shadow type="math_number">
              <field name="NUM">9</field>
            </shadow>
          </value>
        </block>
        <block type="math_trig">
          <value name="NUM">
            <shadow type="math_number">
              <field name="NUM">45</field>
            </shadow>
          </value>
        </block>
        <block type="math_constant"></block>
      </category>

      <category name="字符串" colour="#8c6b5c">
        <label text="string.h"></label>
        <block type="text_length">
          <value name="VALUE">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_indexOf">
          <value name="VALUE">
            <block type="variables_get">
              <field name="VAR">text</field>
            </block>
          </value>
          <value name="FIND">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
        <block type="text_getSubstring">
          <value name="STRING">
            <block type="variables_get">
              <field name="VAR">text</field>
            </block>
          </value>
        </block>
        <block type="text_changeCase">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">abc</field>
            </shadow>
          </value>
        </block>
      </category>

      <category name="标准IO" colour="#8c6b5c">
        <label text="stdio.h"></label>
        <block type="text_print">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">Hello C</field>
            </shadow>
          </value>
        </block>
        <block type="text_prompt_ext">
          <value name="TEXT">
            <shadow type="text">
              <field name="TEXT">输入</field>
            </shadow>
          </value>
        </block>
      </category>
  ` : '';

  const toolboxXml = `
<xml id="toolbox" style="display: none">
  <category name="标准" colour="#5b80a5">
    <category name="逻辑" colour="#5b80a5">
      <block type="controls_if"></block>
      <block type="controls_if">
        <mutation else="1"></mutation>
      </block>
      <block type="logic_compare"></block>
      <block type="logic_operation"></block>
      <block type="logic_negate"></block>
      <block type="logic_boolean"></block>
      <block type="logic_null"></block>
      <block type="logic_ternary"></block>
    </category>

    <category name="循环" colour="#5ba55b">
      <block type="controls_repeat_ext">
        <value name="TIMES">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="controls_whileUntil"></block>
      <block type="controls_for">
        <value name="FROM">
          <shadow type="math_number">
            <field name="NUM">1</field>
          </shadow>
        </value>
        <value name="TO">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
        <value name="BY">
          <shadow type="math_number">
            <field name="NUM">1</field>
          </shadow>
        </value>
      </block>
      <block type="controls_forEach"></block>
      <block type="controls_flow_statements"></block>
    </category>

    <category name="数学" colour="#5b67a5">
    <block type="math_number">
      <field name="NUM">0</field>
    </block>
    <block type="math_arithmetic">
      <value name="A">
        <shadow type="math_number">
          <field name="NUM">1</field>
        </shadow>
      </value>
      <value name="B">
        <shadow type="math_number">
          <field name="NUM">1</field>
        </shadow>
      </value>
    </block>
    <block type="math_single">
      <value name="NUM">
        <shadow type="math_number">
          <field name="NUM">9</field>
        </shadow>
      </value>
    </block>
    <block type="math_trig">
      <value name="NUM">
        <shadow type="math_number">
          <field name="NUM">45</field>
        </shadow>
      </value>
    </block>
    <block type="math_constant"></block>
    <block type="math_number_property">
      <value name="NUMBER_TO_CHECK">
        <shadow type="math_number">
          <field name="NUM">0</field>
        </shadow>
      </value>
    </block>
    <block type="math_round">
      <value name="NUM">
        <shadow type="math_number">
          <field name="NUM">3.1</field>
        </shadow>
      </value>
    </block>
    <block type="math_modulo">
      <value name="DIVIDEND">
        <shadow type="math_number">
          <field name="NUM">64</field>
        </shadow>
      </value>
      <value name="DIVISOR">
        <shadow type="math_number">
          <field name="NUM">10</field>
        </shadow>
      </value>
    </block>
    <block type="math_constrain">
      <value name="VALUE">
        <shadow type="math_number">
          <field name="NUM">50</field>
        </shadow>
      </value>
      <value name="LOW">
        <shadow type="math_number">
          <field name="NUM">1</field>
        </shadow>
      </value>
      <value name="HIGH">
        <shadow type="math_number">
          <field name="NUM">100</field>
        </shadow>
      </value>
    </block>
    <block type="math_random_int">
      <value name="FROM">
        <shadow type="math_number">
          <field name="NUM">1</field>
        </shadow>
      </value>
      <value name="TO">
        <shadow type="math_number">
          <field name="NUM">100</field>
        </shadow>
      </value>
    </block>
    <block type="math_random_float"></block>
    </category>

    <category name="文本" colour="#5ba58c">
    <block type="text"></block>
    <block type="text_number_to_string_custom">
      <value name="VALUE">
        <shadow type="math_number">
          <field name="NUM">123</field>
        </shadow>
      </value>
    </block>
    <block type="text_concat_two_custom">
      <value name="LEFT">
        <shadow type="text">
          <field name="TEXT">hello </field>
        </shadow>
      </value>
      <value name="RIGHT">
        <shadow type="text">
          <field name="TEXT">world</field>
        </shadow>
      </value>
    </block>
    <block type="text_join"></block>
    <block type="text_append">
      <value name="TEXT">
        <shadow type="text"></shadow>
      </value>
    </block>
    <block type="text_length">
      <value name="VALUE">
        <shadow type="text">
          <field name="TEXT">abc</field>
        </shadow>
      </value>
    </block>
    <block type="text_isEmpty">
      <value name="VALUE">
        <shadow type="text">
          <field name="TEXT"></field>
        </shadow>
      </value>
    </block>
    <block type="text_indexOf">
      <value name="VALUE">
        <block type="variables_get">
          <field name="VAR">text</field>
        </block>
      </value>
      <value name="FIND">
        <shadow type="text">
          <field name="TEXT">abc</field>
        </shadow>
      </value>
    </block>
    <block type="text_charAt">
      <value name="VALUE">
        <block type="variables_get">
          <field name="VAR">text</field>
        </block>
      </value>
    </block>
    <block type="text_getSubstring">
      <value name="STRING">
        <block type="variables_get">
          <field name="VAR">text</field>
        </block>
      </value>
    </block>
    <block type="text_changeCase">
      <value name="TEXT">
        <shadow type="text">
          <field name="TEXT">abc</field>
        </shadow>
      </value>
    </block>
    <block type="text_trim">
      <value name="TEXT">
        <shadow type="text">
          <field name="TEXT">abc</field>
        </shadow>
      </value>
    </block>
    <block type="text_print">
      <value name="TEXT">
        <shadow type="text">
          <field name="TEXT">Hello!</field>
        </shadow>
      </value>
    </block>
    <block type="text_prompt_ext">
      <value name="TEXT">
        <shadow type="text">
          <field name="TEXT">请输入</field>
        </shadow>
      </value>
    </block>
    </category>

    <category name="JSON" colour="#4f8f72">
      <block type="json_parse_text">
        <value name="TEXT">
          <shadow type="text">
            <field name="TEXT">{"name":"YiBlock","score":100}</field>
          </shadow>
        </value>
      </block>
      <block type="json_stringify_object">
        <value name="VALUE">
          <block type="variables_get">
            <field name="VAR">jsonObj</field>
          </block>
        </value>
      </block>
      <block type="json_create_object_var"></block>
      <block type="json_create_array_var"></block>
      <block type="json_parse"></block>
      <block type="json_delete"></block>
      <block type="json_object_get"></block>
      <block type="json_object_set_string"></block>
      <block type="json_object_set_number"></block>
      <block type="json_object_set_bool"></block>
      <block type="json_object_set_null"></block>
      <block type="json_object_set_json"></block>
      <block type="json_array_size"></block>
      <block type="json_array_get"></block>
      <block type="json_array_add_string"></block>
      <block type="json_array_add_number"></block>
      <block type="json_array_add_bool"></block>
      <block type="json_array_add_null"></block>
      <block type="json_array_add_json"></block>
      <block type="json_array_remove"></block>
      <block type="json_as_string"></block>
      <block type="json_as_number"></block>
      <block type="json_as_bool"></block>
    </category>

    <category name="变量" colour="#a55b80" custom="VARIABLE"></category>

    <category name="函数" colour="#995ba5" custom="PROCEDURE"></category>

    <category name="列表" colour="#745ba5">
    <block type="lists_create_with">
      <mutation items="0"></mutation>
    </block>
    <block type="lists_create_with">
      <mutation items="3"></mutation>
    </block>
    <block type="lists_repeat">
      <value name="NUM">
        <shadow type="math_number">
          <field name="NUM">5</field>
        </shadow>
      </value>
    </block>
    <block type="lists_length"></block>
    <block type="lists_isEmpty"></block>
    <block type="lists_indexOf">
      <value name="VALUE">
        <block type="variables_get">
          <field name="VAR">list</field>
        </block>
      </value>
    </block>
    <block type="lists_getIndex">
      <value name="VALUE">
        <block type="variables_get">
          <field name="VAR">list</field>
        </block>
      </value>
    </block>
    <block type="lists_setIndex">
      <value name="LIST">
        <block type="variables_get">
          <field name="VAR">list</field>
        </block>
      </value>
    </block>
    <block type="lists_getSublist">
      <value name="LIST">
        <block type="variables_get">
          <field name="VAR">list</field>
        </block>
      </value>
    </block>
    <block type="lists_split">
      <value name="DELIM">
        <shadow type="text">
          <field name="TEXT">,</field>
        </shadow>
      </value>
    </block>
    <block type="lists_sort"></block>
    </category>

    <category name="颜色" colour="#a5745b">
    <block type="colour_picker"></block>
    <block type="colour_random"></block>
    <block type="colour_rgb">
      <value name="RED">
        <shadow type="math_number">
          <field name="NUM">100</field>
        </shadow>
      </value>
      <value name="GREEN">
        <shadow type="math_number">
          <field name="NUM">50</field>
        </shadow>
      </value>
      <value name="BLUE">
        <shadow type="math_number">
          <field name="NUM">0</field>
        </shadow>
      </value>
    </block>
    <block type="colour_blend">
      <value name="COLOUR1">
        <shadow type="colour_picker">
          <field name="COLOUR">#ff0000</field>
        </shadow>
      </value>
      <value name="COLOUR2">
        <shadow type="colour_picker">
          <field name="COLOUR">#3333ff</field>
        </shadow>
      </value>
      <value name="RATIO">
        <shadow type="math_number">
          <field name="NUM">0.5</field>
        </shadow>
      </value>
    </block>
    </category>
  </category>

  <category name="IoT" colour="#3d8ec9">
    <category name="WiFi" colour="#4aa3df">
      <block type="iot_wifi_enable"></block>
      <block type="iot_wifi_set_ssid_password">
        <value name="SSID">
          <shadow type="text">
            <field name="TEXT">my-ssid</field>
          </shadow>
        </value>
        <value name="PASSWORD">
          <shadow type="text">
            <field name="TEXT">my-password</field>
          </shadow>
        </value>
      </block>
      <block type="iot_wifi_connect"></block>
      <block type="iot_wifi_is_connected"></block>
    </category>

    <category name="MQTT" colour="#3d8ec9">
      <block type="iot_mqtt_init">
        <value name="HOST">
          <shadow type="text">
            <field name="TEXT">broker.emqx.io</field>
          </shadow>
        </value>
        <value name="PORT">
          <shadow type="math_number">
            <field name="NUM">1883</field>
          </shadow>
        </value>
        <value name="CLIENT_ID">
          <shadow type="text">
            <field name="TEXT">yiblock2</field>
          </shadow>
        </value>
        <value name="USERNAME">
          <shadow type="text">
            <field name="TEXT">user</field>
          </shadow>
        </value>
        <value name="PASSWORD">
          <shadow type="text">
            <field name="TEXT">${MQTT_RUNTIME_PASSWORD_PLACEHOLDER}</field>
          </shadow>
        </value>
      </block>
      <block type="iot_mqtt_connect"></block>
      <block type="iot_mqtt_is_connected"></block>
      <block type="iot_mqtt_subscribe">
        <value name="TOPIC">
          <shadow type="text">
            <field name="TEXT">demo/topic</field>
          </shadow>
        </value>
      </block>
      <block type="iot_mqtt_publish">
        <value name="TOPIC">
          <shadow type="text">
            <field name="TEXT">demo/topic</field>
          </shadow>
        </value>
        <value name="PAYLOAD">
          <shadow type="text">
            <field name="TEXT">hello</field>
          </shadow>
        </value>
      </block>
      <block type="iot_mqtt_disconnect"></block>
    </category>

  </category>

  <category name="电脑" colour="#3d8ec9">
    <category name="3D" colour="#8e44ad">
      <block type="three_create_object"></block>
      <block type="three_transform"></block>
      <block type="three_on_key"></block>
      <block type="three_on_collision"></block>
    </category>

    <category name="无线外设" colour="#2f8fdb">
      ${wirelessPeripheralGroups || '<label text="请先在左侧外设中添加无线外设"></label>'}
    </category>

    <category name="有线外设" colour="#5c8c6b">
      ${wiredPeripheralBlocks || '<label text="请先在左侧外设中添加有线外设"></label>'}
    </category>

    <category name="UI" colour="#9a5acb" custom="${getYcUiCategoryCallbackKey()}"></category>
  </category>

  ${shouldShowYiOsCategory ? `
  <category name="yiOS" colour="#4d7d72">
    <block type="yios_int2str">
      <value name="VALUE">
        <shadow type="math_number">
          <field name="NUM">123</field>
        </shadow>
      </value>
    </block>
    <block type="yios_str2int">
      <value name="VALUE">
        <shadow type="text">
          <field name="TEXT">123</field>
        </shadow>
      </value>
    </block>
    <block type="yios_str2float">
      <value name="VALUE">
        <shadow type="text">
          <field name="TEXT">3.14</field>
        </shadow>
      </value>
    </block>
    <block type="yios_float2str">
      <value name="VALUE">
        <shadow type="math_number">
          <field name="NUM">3.14</field>
        </shadow>
      </value>
    </block>
  </category>
  ` : ''}

  ${shouldShowCLanguage ? `
  <category name="C语言" colour="#8c6b5c">
${cStdLibCategory}
  </category>
  ` : ''}

  <category name="舞台" colour="#ff8c00">
    <category name="舞台设置" colour="#ff8c00">
      <block type="scratch_stage_switch_backdrop">
        <value name="BACKDROP">
          <shadow type="text">
            <field name="TEXT">背景1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_stage_next_backdrop"></block>
      <block type="scratch_stage_set_mode"></block>
      <block type="scratch_stage_set_speech_voice"></block>
      <block type="scratch_stage_show"></block>
      <block type="scratch_stage_hide"></block>
    </category>

    <category name="控制" colour="#ffab19">
      <block type="scratch_event_when_green_flag"></block>
      <block type="scratch_event_when_key_pressed"></block>
      <block type="scratch_event_when_sprite_clicked"></block>
      <block type="scratch_control_system_beep">
        <value name="DURATION">
          <shadow type="math_number">
            <field name="NUM">200</field>
          </shadow>
        </value>
        <value name="FREQ">
          <shadow type="math_number">
            <field name="NUM">880</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_wait">
        <value name="SECS">
          <shadow type="math_number">
            <field name="NUM">1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_wait_until">
        <value name="CONDITION">
          <shadow type="logic_boolean">
            <field name="BOOL">TRUE</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_repeat">
        <value name="TIMES">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_forever"></block>
      <block type="scratch_control_repeat_until">
        <value name="CONDITION">
          <shadow type="logic_boolean">
            <field name="BOOL">FALSE</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_stop"></block>
      <block type="scratch_control_create_clone_of">
        <value name="TARGET">
          <shadow type="text">
            <field name="TEXT">自己</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_control_delete_clone"></block>
      <block type="scratch_event_when_clone_start"></block>
    </category>

    <category name="列表" colour="#9966ff">
      <block type="scratch_list_add"></block>
      <block type="scratch_list_delete"></block>
      <block type="scratch_list_insert"></block>
      <block type="scratch_list_replace"></block>
      <block type="scratch_list_item"></block>
      <block type="scratch_list_length"></block>
      <block type="scratch_list_index_of"></block>
      <block type="scratch_list_contents"></block>
      <block type="scratch_list_contains"></block>
      <block type="scratch_list_clear"></block>
      <block type="scratch_list_show"></block>
      <block type="scratch_list_hide"></block>
    </category>

    <category name="画笔" colour="#0fbd8c">
      <block type="scratch_pen_clear"></block>
      <block type="scratch_pen_stamp"></block>
      <block type="scratch_pen_down"></block>
      <block type="scratch_pen_up"></block>
      <block type="scratch_pen_set_color"></block>
      <block type="scratch_pen_change_size"></block>
      <block type="scratch_pen_set_size"></block>
    </category>

    <category name="音乐" colour="#cf63cf">
      <block type="scratch_music_play_note"></block>
      <block type="scratch_music_rest"></block>
      <block type="scratch_music_play_drum"></block>
      <block type="scratch_music_set_instrument"></block>
      <block type="scratch_music_set_tempo"></block>
      <block type="scratch_music_change_tempo"></block>
      <block type="scratch_music_tempo"></block>
    </category>

    <category name="视频" colour="#5cb1d6">
      <block type="scratch_video_turn"></block>
      <block type="scratch_video_set_transparency"></block>
      <block type="scratch_video_motion"></block>
      <block type="scratch_video_direction"></block>
    </category>

    <category name="硬件" colour="#4c97ff">
      <block type="scratch_hardware_when_button"></block>
      <block type="scratch_hardware_display_text"></block>
      <block type="scratch_hardware_acceleration"></block>
    </category>

    <category name="运动" colour="#4c97ff">
      <block type="scratch_motion_move_steps">
        <value name="STEPS">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_turn_right">
        <value name="DEGREES">
          <shadow type="math_number">
            <field name="NUM">15</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_turn_left">
        <value name="DEGREES">
          <shadow type="math_number">
            <field name="NUM">15</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_go_to_xy">
        <value name="X">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
        <value name="Y">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_glide_to_xy">
        <value name="SECS">
          <shadow type="math_number">
            <field name="NUM">1</field>
          </shadow>
        </value>
        <value name="X">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
        <value name="Y">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_point_in_direction">
        <value name="DIRECTION">
          <shadow type="math_number">
            <field name="NUM">90</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_set_x">
        <value name="X">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_set_y">
        <value name="Y">
          <shadow type="math_number">
            <field name="NUM">0</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_change_x">
        <value name="DX">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_change_y">
        <value name="DY">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_motion_if_on_edge_bounce"></block>
      <block type="scratch_motion_x_position"></block>
      <block type="scratch_motion_y_position"></block>
      <block type="scratch_motion_direction"></block>
    </category>

    <category name="外观" colour="#9966ff">
      <block type="scratch_looks_say">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">Hello!</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_say_for_secs">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">Hello!</field>
          </shadow>
        </value>
        <value name="SECS">
          <shadow type="math_number">
            <field name="NUM">2</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_think">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">Hmm...</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_think_for_secs">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">Hmm...</field>
          </shadow>
        </value>
        <value name="SECS">
          <shadow type="math_number">
            <field name="NUM">2</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_show"></block>
      <block type="scratch_looks_hide"></block>
      <block type="scratch_looks_switch_costume">
        <value name="COSTUME">
          <shadow type="text">
            <field name="TEXT">造型1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_next_costume"></block>
      <block type="scratch_looks_change_size">
        <value name="SIZE">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_set_size">
        <value name="SIZE">
          <shadow type="math_number">
            <field name="NUM">100</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_looks_size"></block>
    </category>

    <category name="声音" colour="#d65cd6">
      <block type="scratch_sound_play">
        <value name="SOUND">
          <shadow type="text">
            <field name="TEXT">sound1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_sound_play_until_done">
        <value name="SOUND">
          <shadow type="text">
            <field name="TEXT">sound1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_sound_stop_all"></block>
      <block type="scratch_sound_change_volume">
        <value name="VOLUME">
          <shadow type="math_number">
            <field name="NUM">10</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_sound_set_volume">
        <value name="VOLUME">
          <shadow type="math_number">
            <field name="NUM">100</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_sound_volume"></block>
    </category>

    <category name="事件" colour="#ffbf00">
      <block type="scratch_event_broadcast">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">message1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_event_broadcast_and_wait">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">message1</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_event_when_receive">
        <value name="MESSAGE">
          <shadow type="text">
            <field name="TEXT">message1</field>
          </shadow>
        </value>
      </block>
    </category>

    <category name="侦测" colour="#5cb1d6">
      <block type="scratch_sensing_touching"></block>
      <block type="scratch_sensing_touching_color"></block>
      <block type="scratch_sensing_color_touching_color"></block>
      <block type="scratch_sensing_distance_to"></block>
      <block type="scratch_sensing_ask_and_wait">
        <value name="QUESTION">
          <shadow type="text">
            <field name="TEXT">你的名字？</field>
          </shadow>
        </value>
      </block>
      <block type="scratch_sensing_answer"></block>
      <block type="scratch_sensing_key_pressed"></block>
      <block type="scratch_sensing_mouse_down"></block>
      <block type="scratch_sensing_mouse_x"></block>
      <block type="scratch_sensing_mouse_y"></block>
      <block type="scratch_sensing_loudness"></block>
      <block type="scratch_sensing_timer"></block>
      <block type="scratch_sensing_reset_timer"></block>
      <block type="scratch_sensing_current_time"></block>
      <block type="scratch_sensing_days_since_2000"></block>
    </category>
  </category>
</xml>
  `.trim();

  return localizeToolboxXml(toolboxXml, uiLanguage);
}

/**
 * 生成工具箱JSON格式（Blockly 10+ 推荐）
 */
export function getToolboxJson(
  uiLanguage: ResolvedUiLanguage = getResolvedUiLanguageFromStorage()
) {
  return {
    kind: 'categoryToolbox',
    contents: [
      {
        kind: 'category',
        name: localizeCategoryName('逻辑', uiLanguage),
        colour: '#5b80a5',
        contents: [
          { kind: 'block', type: 'controls_if' },
          { kind: 'block', type: 'logic_compare' },
          { kind: 'block', type: 'logic_operation' },
          { kind: 'block', type: 'logic_negate' },
          { kind: 'block', type: 'logic_boolean' },
          { kind: 'block', type: 'logic_null' },
          { kind: 'block', type: 'logic_ternary' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('循环', uiLanguage),
        colour: '#5ba55b',
        contents: [
          { kind: 'block', type: 'controls_repeat_ext' },
          { kind: 'block', type: 'controls_whileUntil' },
          { kind: 'block', type: 'controls_for' },
          { kind: 'block', type: 'controls_forEach' },
          { kind: 'block', type: 'controls_flow_statements' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('数学', uiLanguage),
        colour: '#5b67a5',
        contents: [
          { kind: 'block', type: 'math_number' },
          { kind: 'block', type: 'math_arithmetic' },
          { kind: 'block', type: 'math_single' },
          { kind: 'block', type: 'math_trig' },
          { kind: 'block', type: 'math_constant' },
          { kind: 'block', type: 'math_number_property' },
          { kind: 'block', type: 'math_round' },
          { kind: 'block', type: 'math_modulo' },
          { kind: 'block', type: 'math_constrain' },
          { kind: 'block', type: 'math_random_int' },
          { kind: 'block', type: 'math_random_float' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('文本', uiLanguage),
        colour: '#5ba58c',
        contents: [
          { kind: 'block', type: 'text' },
          { kind: 'block', type: 'text_join' },
          { kind: 'block', type: 'text_append' },
          { kind: 'block', type: 'text_length' },
          { kind: 'block', type: 'text_isEmpty' },
          { kind: 'block', type: 'text_indexOf' },
          { kind: 'block', type: 'text_charAt' },
          { kind: 'block', type: 'text_getSubstring' },
          { kind: 'block', type: 'text_changeCase' },
          { kind: 'block', type: 'text_trim' },
          { kind: 'block', type: 'text_print' },
          { kind: 'block', type: 'text_prompt_ext' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('JSON', uiLanguage),
        colour: '#4f8f72',
        contents: [
          { kind: 'block', type: 'json_parse_text' },
          { kind: 'block', type: 'json_stringify_object' },
          { kind: 'block', type: 'json_create_object_var' },
          { kind: 'block', type: 'json_create_array_var' },
          { kind: 'block', type: 'json_parse' },
          { kind: 'block', type: 'json_delete' },
          { kind: 'block', type: 'json_object_get' },
          { kind: 'block', type: 'json_object_set_string' },
          { kind: 'block', type: 'json_object_set_number' },
          { kind: 'block', type: 'json_object_set_bool' },
          { kind: 'block', type: 'json_object_set_null' },
          { kind: 'block', type: 'json_object_set_json' },
          { kind: 'block', type: 'json_array_size' },
          { kind: 'block', type: 'json_array_get' },
          { kind: 'block', type: 'json_array_add_string' },
          { kind: 'block', type: 'json_array_add_number' },
          { kind: 'block', type: 'json_array_add_bool' },
          { kind: 'block', type: 'json_array_add_null' },
          { kind: 'block', type: 'json_array_add_json' },
          { kind: 'block', type: 'json_array_remove' },
          { kind: 'block', type: 'json_as_string' },
          { kind: 'block', type: 'json_as_number' },
          { kind: 'block', type: 'json_as_bool' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('变量', uiLanguage),
        colour: '#a55b80',
        custom: 'VARIABLE',
      },
      {
        kind: 'category',
        name: localizeCategoryName('函数', uiLanguage),
        colour: '#995ba5',
        custom: 'PROCEDURE',
      },
      {
        kind: 'category',
        name: localizeCategoryName('列表', uiLanguage),
        colour: '#745ba5',
        contents: [
          { kind: 'block', type: 'lists_create_with' },
          { kind: 'block', type: 'lists_repeat' },
          { kind: 'block', type: 'lists_length' },
          { kind: 'block', type: 'lists_isEmpty' },
          { kind: 'block', type: 'lists_indexOf' },
          { kind: 'block', type: 'lists_getIndex' },
          { kind: 'block', type: 'lists_setIndex' },
          { kind: 'block', type: 'lists_getSublist' },
          { kind: 'block', type: 'lists_split' },
          { kind: 'block', type: 'lists_sort' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('颜色', uiLanguage),
        colour: '#a5745b',
        contents: [
          { kind: 'block', type: 'colour_picker' },
          { kind: 'block', type: 'colour_random' },
          { kind: 'block', type: 'colour_rgb' },
          { kind: 'block', type: 'colour_blend' },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('IoT', uiLanguage),
        colour: '#3d8ec9',
        contents: [
          {
            kind: 'category',
            name: localizeCategoryName('WiFi', uiLanguage),
            colour: '#4aa3df',
            contents: [
              { kind: 'block', type: 'iot_wifi_enable' },
              { kind: 'block', type: 'iot_wifi_set_ssid_password' },
              { kind: 'block', type: 'iot_wifi_connect' },
              { kind: 'block', type: 'iot_wifi_is_connected' },
            ],
          },
          {
            kind: 'category',
            name: localizeCategoryName('MQTT', uiLanguage),
            colour: '#3d8ec9',
            contents: [
              { kind: 'block', type: 'iot_mqtt_init' },
              { kind: 'block', type: 'iot_mqtt_connect' },
              { kind: 'block', type: 'iot_mqtt_is_connected' },
              { kind: 'block', type: 'iot_mqtt_subscribe' },
              { kind: 'block', type: 'iot_mqtt_publish' },
              { kind: 'block', type: 'iot_mqtt_disconnect' },
            ],
          },
        ],
      },
      {
        kind: 'category',
        name: localizeCategoryName('3D', uiLanguage),
        colour: '#8e44ad',
        contents: [
          { kind: 'block', type: 'three_create_object' },
          { kind: 'block', type: 'three_transform' },
          { kind: 'block', type: 'three_on_key' },
          { kind: 'block', type: 'three_on_collision' },
        ],
      },
    ],
  };
}

export default getToolboxXml;
